import React from "react";
import { LAUNDRY_SERVICE, CARPET_SERVICE } from "../../lib/pricing";
import { Calculator, Clock, Sparkles, House, Building2, ChefHat, Flame, Wind, Minus, Plus } from "lucide-react";
import { quoteCalculatorData } from "../../data/mockData";

/* ── Service Type Selector ── */

const ServiceTypeSelector = ({ selectedService, onSelect }) => (
  <div className="space-y-3">
    <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Select Service Type</label>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {quoteCalculatorData.serviceTypes.map((type) => (
        <button
          key={type}
          data-testid={`quote-service-${type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onSelect(type)}
          className={`p-4 rounded-xl border-2 transition-all duration-200 font-semibold ${
            selectedService === type
              ? "border-cleanora-primary bg-gradient-to-br from-cleanora-primary to-cleanora-primary-dark text-white shadow-lg scale-105"
              : "border-slate-200 hover:border-cleanora-primary text-slate-700 hover:shadow-md"
          }`}
        >
          <div className="text-sm">{type}</div>
        </button>
      ))}
    </div>
  </div>
);

/* ── Property Size Slider ── */

const PropertySizeSlider = ({ bedrooms, onChange }) => (
  <div className="space-y-3">
    <div className="flex justify-between items-center">
      <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Bedrooms</label>
      <span className="text-xl font-extrabold text-cleanora-primary">
        {bedrooms} Bedroom{bedrooms > 1 ? "s" : ""}
      </span>
    </div>
    <input
      type="range"
      min="1"
      max="8"
      value={bedrooms}
      data-testid="quote-bedrooms-slider"
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full"
      style={{
        background: `linear-gradient(to right, #0EA5E9 ${((bedrooms - 1) / 7) * 100}%, #e2e8f0 ${((bedrooms - 1) / 7) * 100}%)`,
      }}
    />
    <div className="flex justify-between text-xs text-slate-500 font-semibold">
      <span>1 Bed</span>
      <span>8+ Beds</span>
    </div>
  </div>
);

/* ── Bathrooms Slider ── */

const BathroomsSlider = ({ bathrooms, onChange }) => (
  <div className="space-y-3">
    <div className="flex justify-between items-center">
      <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Bathrooms</label>
      <span className="text-xl font-extrabold text-cleanora-primary">
        {bathrooms} Bathroom{bathrooms > 1 ? "s" : ""}
      </span>
    </div>
    <input
      type="range"
      min="1"
      max="8"
      value={bathrooms}
      data-testid="quote-bathrooms-slider"
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full"
      style={{
        background: `linear-gradient(to right, #0EA5E9 ${((bathrooms - 1) / 7) * 100}%, #e2e8f0 ${((bathrooms - 1) / 7) * 100}%)`,
      }}
    />
    <div className="flex justify-between text-xs text-slate-500 font-semibold">
      <span>1</span>
      <span>8+</span>
    </div>
  </div>
);

/* ── Toilets Slider ── */

const ToiletsSlider = ({ toilets, onChange }) => (
  <div className="space-y-3">
    <div className="flex justify-between items-center">
      <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Separate Toilets</label>
      <span className="text-xl font-extrabold text-cleanora-primary">
        {toilets} Toilet{toilets > 1 ? "s" : ""}
      </span>
    </div>
    <input
      type="range"
      min="0"
      max="8"
      value={toilets}
      data-testid="quote-toilets-slider"
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full"
      style={{
        background: `linear-gradient(to right, #0EA5E9 ${(toilets / 8) * 100}%, #e2e8f0 ${(toilets / 8) * 100}%)`,
      }}
    />
    <div className="flex justify-between text-xs text-slate-500 font-semibold">
      <span>None</span>
      <span>8+</span>
    </div>
  </div>
);

/* ── Frequency Selector ── */

const FrequencySelector = ({ frequency, onSelect, discounts, label = "Cleaning Frequency" }) => (
  <div className="space-y-3">
    <label className="text-sm font-semibold text-slate-700">{label}</label>
    <div className="grid grid-cols-2 gap-3">
      {quoteCalculatorData.frequencies.map((freq) => (
        <button
          key={freq.label}
          data-testid={`quote-frequency-${freq.label.toLowerCase()}`}
          onClick={() => onSelect(freq.label)}
          className={`p-3 rounded-lg border-2 transition-all duration-200 relative ${
            frequency === freq.label
              ? "border-sky-600 bg-sky-50 text-sky-700"
              : "border-slate-200 hover:border-sky-300 text-slate-700"
          }`}
        >
          <div className="text-sm font-semibold">{freq.label}</div>
          {discounts[freq.label] > 0 && (
            <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
              Save {discounts[freq.label]}%
            </span>
          )}
        </button>
      ))}
    </div>
  </div>
);

/* ── Additional Options ── */

const AdditionalOptions = ({ suppliesProvided, setSuppliesProvided, linensIncluded, setLinensIncluded, parkingAvailable, setParkingAvailable, selectedService }) => (
  <div className="space-y-4">
    <label className="text-sm font-semibold text-slate-700">Additional Information</label>
    
    <div className="space-y-3">
      {selectedService !== LAUNDRY_SERVICE && selectedService !== CARPET_SERVICE && (
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={suppliesProvided}
          onChange={(e) => setSuppliesProvided(e.target.checked)}
          data-testid="quote-supplies-checkbox"
          className="mt-1 w-5 h-5 text-sky-600 border-slate-300 rounded focus:ring-sky-600"
        />
        <div>
          <div className="text-sm font-medium text-slate-700 group-hover:text-sky-600 transition-colors">
            I will provide cleaning supplies
          </div>
          <div className="text-xs text-slate-500">
            If unchecked, we'll bring our professional eco-friendly supplies (may affect price)
          </div>
        </div>
      </label>
      )}

      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={parkingAvailable}
          onChange={(e) => setParkingAvailable(e.target.checked)}
          data-testid="quote-parking-checkbox"
          className="mt-1 w-5 h-5 text-sky-600 border-slate-300 rounded focus:ring-sky-600"
        />
        <div>
          <div className="text-sm font-medium text-slate-700 group-hover:text-sky-600 transition-colors">
            There is parking space available
          </div>
          <div className="text-xs text-slate-500">
            Tick if our cleaner can park at or near the property
          </div>
        </div>
      </label>

      {selectedService === "Airbnb Cleaning" && (
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={linensIncluded}
            onChange={(e) => setLinensIncluded(e.target.checked)}
            data-testid="quote-linens-checkbox"
            className="mt-1 w-5 h-5 text-sky-600 border-slate-300 rounded focus:ring-sky-600"
          />
          <div>
            <div className="text-sm font-medium text-slate-700 group-hover:text-sky-600 transition-colors">
              Linen change required
            </div>
            <div className="text-xs text-slate-500">
              We'll strip beds, replace linens, and make beds (may add 15-30 min)
            </div>
          </div>
        </label>
      )}
    </div>
  </div>
);

/* ── Oven Cleaning Options ── */

const OvenCleaningOptions = ({ ovenOptions, setOvenOptions, oven }) => {
  const handleChange = (key) => {
    setOvenOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const options = [
    { key: 'singleOven', label: 'Single Oven', icon: ChefHat, price: '+£0' },
    { key: 'doubleOven', label: 'Double Oven', icon: ChefHat, price: `+£${oven.doubleOven}` },
    { key: 'agaRange', label: 'AGA/Range Cooker', icon: Flame, price: `+£${oven.agaRange}` },
    { key: 'hob', label: 'Hob Cleaning', icon: Flame, price: `+£${oven.hob}` },
    { key: 'extractorFan', label: 'Extractor Fan', icon: Wind, price: `+£${oven.extractorFan}` },
  ];

  return (
    <div className="space-y-4">
      <label className="text-sm font-semibold text-slate-700">Select Items to Clean</label>
      <div className="space-y-3">
        {options.map(({ key, label, icon: Icon, price }) => (
          <label key={key} className="flex items-center gap-3 cursor-pointer group p-3 rounded-lg border-2 transition-all duration-200 hover:border-sky-300 has-[:checked]:border-sky-600 has-[:checked]:bg-sky-50">
            <input
              type="checkbox"
              checked={ovenOptions[key]}
              onChange={() => handleChange(key)}
              className="w-5 h-5 text-sky-600 border-slate-300 rounded focus:ring-sky-600"
            />
            <Icon className="w-5 h-5 text-sky-600" />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-700">{label}</div>
            </div>
            <div className="text-sm font-semibold text-sky-600">{price}</div>
          </label>
        ))}
      </div>
      <p className="text-xs text-slate-500 italic">
        * Select at least one item. Prices shown are additional to the base oven cleaning service.
      </p>
    </div>
  );
};

/* ── Laundry Options ── */

const Stepper = ({ label, hint, value, onChange, min = 0, max, step = 1, unit, testId }) => (
  <div className="flex items-center justify-between gap-4 rounded-xl border-2 border-slate-100 p-4">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-700">{label}</div>
      <div className="text-xs text-slate-500">{hint}</div>
    </div>
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        onClick={() => onChange(Math.max(min, value - step))}
        disabled={value <= min}
        data-testid={`${testId}-minus`}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-600 disabled:opacity-40"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="w-14 text-center text-lg font-bold text-sky-600" data-testid={`${testId}-value`}>
        {value}{unit}
      </span>
      <button
        type="button"
        aria-label={`Increase ${label}`}
        onClick={() => onChange(Math.min(max, value + step))}
        disabled={value >= max}
        data-testid={`${testId}-plus`}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-600 disabled:opacity-40"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const LaundryOptions = ({ laundry, setLaundry, rates }) => {
  const set = (key) => (v) => setLaundry((prev) => ({ ...prev, [key]: v }));
  return (
    <div className="space-y-3" data-testid="quote-laundry-options">
      <label className="text-sm font-semibold text-slate-700">What needs doing?</label>
      <Stepper label="Wash, dry & fold" hint={`£${rates.perKg} per kg`} value={laundry.kg} onChange={set("kg")} max={50} unit=" kg" testId="quote-laundry-kg" />
      <Stepper label="Ironing" hint={`£${rates.ironingItem} per item`} value={laundry.ironing} onChange={set("ironing")} max={100} testId="quote-laundry-ironing" />
      <Stepper label="Duvets" hint={`£${rates.duvet} each`} value={laundry.duvets} onChange={set("duvets")} max={10} testId="quote-laundry-duvets" />
      <Stepper label="2-piece suit dry clean" hint={`£${rates.suitDryClean} each`} value={laundry.suits} onChange={set("suits")} max={10} testId="quote-laundry-suits" />
    </div>
  );
};

/* ── Carpet Options ── */

const CarpetOptions = ({ carpet, setCarpet, rates }) => {
  const set = (key) => (v) => setCarpet((prev) => ({ ...prev, [key]: v }));
  return (
    <div className="space-y-3" data-testid="quote-carpet-options">
      <label className="text-sm font-semibold text-slate-700">Which carpets need cleaning?</label>
      <Stepper label="Bedrooms / rooms" hint={`£${rates.room} per room`} value={carpet.rooms} onChange={set("rooms")} max={20} testId="quote-carpet-rooms" />
      <Stepper label="Living room" hint={`£${rates.livingRoom} flat rate`} value={carpet.livingRooms} onChange={set("livingRooms")} max={5} testId="quote-carpet-living" />
      <Stepper label="Stairs & hallways" hint={`£${rates.stairsHallway} per flight / hallway`} value={carpet.stairsHallways} onChange={set("stairsHallways")} max={10} testId="quote-carpet-stairs" />
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border-2 border-slate-100 p-4">
        <input
          type="checkbox"
          checked={carpet.stainProtection}
          onChange={(e) => setCarpet((prev) => ({ ...prev, stainProtection: e.target.checked }))}
          data-testid="quote-carpet-stain-checkbox"
          className="mt-1 h-5 w-5 rounded border-slate-300 text-sky-600 focus:ring-sky-600"
        />
        <div>
          <div className="text-sm font-semibold text-slate-700">Stain protection / pet odour treatment (+£{rates.stainProtection})</div>
          <div className="text-xs text-slate-500">Optional add-on, applied to the whole job</div>
        </div>
      </label>
    </div>
  );
};

/* ── Commercial Cleaning Fields ── */

const CommercialCleaningFields = ({ squareFootage, setSquareFootage, numberOfOffices, setNumberOfOffices }) => (
  <div className="space-y-6">
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-semibold text-slate-700">Estimated Square Footage</label>
        <span className="text-lg font-bold text-sky-600">
          {squareFootage.toLocaleString()} sq ft
        </span>
      </div>
      <input
        type="range"
        min="500"
        max="10000"
        step="500"
        value={squareFootage}
        onChange={(e) => setSquareFootage(parseInt(e.target.value))}
        className="w-full"
        style={{
          background: `linear-gradient(to right, #0284c7 ${((squareFootage - 500) / 9500) * 100}%, #e2e8f0 ${((squareFootage - 500) / 9500) * 100}%)`,
        }}
      />
      <div className="flex justify-between text-xs text-slate-500">
        <span>500 sq ft</span>
        <span>10,000+ sq ft</span>
      </div>
    </div>

    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-semibold text-slate-700">Number of Offices/Rooms</label>
        <span className="text-lg font-bold text-sky-600">
          {numberOfOffices} {numberOfOffices === 1 ? 'Office' : 'Offices'}
        </span>
      </div>
      <input
        type="range"
        min="1"
        max="20"
        value={numberOfOffices}
        onChange={(e) => setNumberOfOffices(parseInt(e.target.value))}
        className="w-full"
        style={{
          background: `linear-gradient(to right, #0284c7 ${((numberOfOffices - 1) / 19) * 100}%, #e2e8f0 ${((numberOfOffices - 1) / 19) * 100}%)`,
        }}
      />
      <div className="flex justify-between text-xs text-slate-500">
        <span>1 Office</span>
        <span>20+ Offices</span>
      </div>
    </div>
  </div>
);

/* ── Price Breakdown ── */

const BreakdownRow = ({ label, value, testId, muted }) => (
  <li className={`flex items-baseline justify-between gap-4 ${muted ? "text-emerald-200" : ""}`} data-testid={testId}>
    <span className="opacity-90">{label}</span>
    <span className="font-semibold tabular-nums">{value}</span>
  </li>
);

const PriceBreakdown = ({ breakdown }) => (
  <div className="rounded-xl bg-white/10 p-4 text-sm" data-testid="quote-price-breakdown">
    <div className="mb-2 text-xs font-bold uppercase tracking-wide opacity-80">How we worked it out</div>
    <ul className="space-y-1.5">
      {breakdown.lines.map((line, i) => (
        <BreakdownRow
          key={line.label}
          label={line.label}
          value={i === 0 ? `£${line.amount}` : `+£${line.amount}`}
          testId={`quote-breakdown-line-${i}`}
        />
      ))}
      {breakdown.discount > 0 && (
        <BreakdownRow
          muted
          label={`${breakdown.frequency} discount (${breakdown.discountPct}%)`}
          value={`-£${breakdown.discount}`}
          testId="quote-breakdown-discount"
        />
      )}
    </ul>
  </div>
);

/* ── Price Display Card ── */

const PriceDisplay = ({ estimatedPrice, estimatedDuration, selectedService, breakdown }) => (
  <div className="bg-gradient-to-br from-cleanora-primary via-cleanora-primary-dark to-cleanora-primary-dark text-white rounded-2xl p-8 space-y-6 shadow-2xl">
    <div className="text-center space-y-3">
      <div className="text-sm font-semibold opacity-90 uppercase tracking-wide">Estimated Price</div>
      <div className="text-6xl font-extrabold" data-testid="quote-estimated-price">£{estimatedPrice}</div>
      <div className="text-sm opacity-90 font-medium">{selectedService === LAUNDRY_SERVICE ? "per order" : selectedService === CARPET_SERVICE ? "per job" : "per visit"}</div>
    </div>
    {breakdown && <PriceBreakdown breakdown={breakdown} />}
    <div className="border-t border-white/20 pt-6 space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 font-medium">
          <Clock className="w-5 h-5" />
          Est. Duration
        </span>
        <span className="font-bold" data-testid="quote-estimated-duration">{estimatedDuration}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 font-medium">
          <Sparkles className="w-5 h-5" />
          Service
        </span>
        <span className="font-bold">{selectedService}</span>
      </div>
    </div>
    <button
      onClick={() => {
        const form = document.querySelector("#quote-form");
        if (form) form.scrollIntoView({ behavior: "smooth" });
      }}
      className="w-full bg-white text-cleanora-primary hover:bg-cleanora-soft-mist text-lg py-4 font-extrabold shadow-lg rounded-xl transition-all duration-300 hover:scale-105"
    >
      Get Accurate Quote
    </button>
    <p className="text-xs text-center opacity-75">
      * This is an estimate. Final price may vary based on property condition
    </p>
  </div>
);

/* ── Trust Badges ── */

const TrustBadges = () => (
  <div className="grid md:grid-cols-3 gap-4 pt-4">
    <div className="flex items-center gap-2 text-slate-600">
      <House className="w-5 h-5 text-cleanora-primary" />
      <span className="text-sm font-bold">Professional Service</span>
    </div>
    <div className="flex items-center gap-2 text-slate-600">
      <Sparkles className="w-5 h-5 text-cleanora-primary" />
      <span className="text-sm font-bold">Quality Guaranteed</span>
    </div>
    <div className="flex items-center gap-2 text-slate-600">
      <Building2 className="w-5 h-5 text-cleanora-primary" />
      <span className="text-sm font-bold">Fully Insured</span>
    </div>
  </div>
);

/* ── Main Calculator ── */

const QuoteCalculator = ({
  selectedService,
  setSelectedService,
  bedrooms,
  setBedrooms,
  bathrooms,
  setBathrooms,
  toilets,
  setToilets,
  frequency,
  setFrequency,
  suppliesProvided,
  setSuppliesProvided,
  linensIncluded,
  setLinensIncluded,
  parkingAvailable,
  setParkingAvailable,
  ovenOptions,
  setOvenOptions,
  squareFootage,
  setSquareFootage,
  numberOfOffices,
  setNumberOfOffices,
  estimatedPrice,
  estimatedDuration,
  pricing,
  breakdown,
  laundry,
  setLaundry,
  carpet,
  setCarpet,
}) => {
  const isLaundry = selectedService === LAUNDRY_SERVICE;
  const isCarpet = selectedService === CARPET_SERVICE;
  const isOvenCleaning = selectedService === "Oven Cleaning";
  const isCommercialCleaning = selectedService === "Commercial Cleaning";
  
  return (
    <div className="rounded-2xl bg-white shadow-2xl border-2 border-cleanora-primary/20">
      <div className="p-8 bg-gradient-to-r from-cleanora-soft-mist to-white pb-6 rounded-t-2xl border-b-2 border-cleanora-primary/10">
        <div className="flex items-center gap-3 text-2xl font-bold">
          <div className="w-14 h-14 bg-gradient-to-br from-cleanora-primary to-cleanora-primary-dark rounded-2xl flex items-center justify-center shadow-lg">
            <Calculator className="w-7 h-7 text-white" />
          </div>
          <span className="text-slate-900">Estimate Your Price</span>
        </div>
        <p className="text-slate-600 mt-3 font-medium">Get an instant quote based on your needs</p>
      </div>

      <div className="p-8 space-y-8">
        <ServiceTypeSelector selectedService={selectedService} onSelect={setSelectedService} />
        
        {/* Conditional rendering based on service type */}
        {isCarpet ? (
          <CarpetOptions carpet={carpet} setCarpet={setCarpet} rates={pricing.carpet} />
        ) : isLaundry ? (
          <LaundryOptions laundry={laundry} setLaundry={setLaundry} rates={pricing.laundry} />
        ) : isOvenCleaning ? (
          // Oven Cleaning: Show oven-specific options only
          <OvenCleaningOptions ovenOptions={ovenOptions} setOvenOptions={setOvenOptions} oven={pricing.oven} />
        ) : isCommercialCleaning ? (
          // Commercial Cleaning: Show commercial fields + bathrooms/toilets
          <>
            <CommercialCleaningFields 
              squareFootage={squareFootage}
              setSquareFootage={setSquareFootage}
              numberOfOffices={numberOfOffices}
              setNumberOfOffices={setNumberOfOffices}
            />
            <BathroomsSlider bathrooms={bathrooms} onChange={setBathrooms} />
            <ToiletsSlider toilets={toilets} onChange={setToilets} />
          </>
        ) : (
          // Regular services: Show standard fields
          <>
            <PropertySizeSlider bedrooms={bedrooms} onChange={setBedrooms} />
            <BathroomsSlider bathrooms={bathrooms} onChange={setBathrooms} />
            <ToiletsSlider toilets={toilets} onChange={setToilets} />
          </>
        )}
        
        <FrequencySelector frequency={frequency} onSelect={setFrequency} discounts={pricing.discounts} label={isLaundry ? "Frequency" : "Cleaning Frequency"} />
        <AdditionalOptions 
          suppliesProvided={suppliesProvided}
          setSuppliesProvided={setSuppliesProvided}
          linensIncluded={linensIncluded}
          setLinensIncluded={setLinensIncluded}
          parkingAvailable={parkingAvailable}
          setParkingAvailable={setParkingAvailable}
          selectedService={selectedService}
        />
        <PriceDisplay
          estimatedPrice={estimatedPrice}
          estimatedDuration={estimatedDuration}
          selectedService={selectedService}
          breakdown={breakdown}
        />
        <TrustBadges />
      </div>
    </div>
  );
};

export default QuoteCalculator;
